import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import PersonaAvatar from '../../components/PersonaAvatar'

interface Source {
  name: string
  pending_count: number
  newest_at: string | null
  muted: boolean
  muted_at: string | null
}

interface Category {
  key: string
  label: string
  description: string
  pending_count: number
  newest_at: string | null
  senders: string[]
  muted: boolean
  muted_at: string | null
}

function relativeTime(iso: string | null): string {
  if (!iso) return 'never'
  const then = new Date(iso.replace(' ', 'T') + (iso.includes('Z') ? '' : 'Z')).getTime()
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (s < 60) return `${s}s ago`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  return `${d}d ago`
}

export default function FeedSettingsPage() {
  const qc = useQueryClient()
  const [sourcesOpen, setSourcesOpen] = useState(false)

  const { data: catsData, isLoading: catsLoading } = useQuery<{ categories: Category[] }>({
    queryKey: ['feedCategories'],
    queryFn: async () => {
      const r = await fetch('/api/feed/categories')
      if (!r.ok) throw new Error('categories fetch failed')
      return r.json()
    },
    refetchInterval: 30_000,
  })

  const { data: srcData, isLoading: srcLoading } = useQuery<{ sources: Source[] }>({
    queryKey: ['feedSources'],
    queryFn: async () => {
      const r = await fetch('/api/feed/sources')
      if (!r.ok) throw new Error('sources fetch failed')
      return r.json()
    },
    refetchInterval: 30_000,
    enabled: sourcesOpen,
  })

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['feedCategories'] })
    qc.invalidateQueries({ queryKey: ['feedSources'] })
    qc.invalidateQueries({ queryKey: ['feedCards'] })
    qc.invalidateQueries({ queryKey: ['feedPrefs'] })
  }

  const muteCat = useMutation({
    mutationFn: async (key: string) => {
      const r = await fetch(`/api/feed/categories/${encodeURIComponent(key)}/mute`, { method: 'POST' })
      if (!r.ok) throw new Error('mute failed'); return r.json()
    },
    onSuccess: invalidateAll,
  })
  const unmuteCat = useMutation({
    mutationFn: async (key: string) => {
      const r = await fetch(`/api/feed/categories/${encodeURIComponent(key)}/unmute`, { method: 'POST' })
      if (!r.ok) throw new Error('unmute failed'); return r.json()
    },
    onSuccess: invalidateAll,
  })
  const muteSrc = useMutation({
    mutationFn: async (n: string) => {
      const r = await fetch(`/api/feed/sources/${encodeURIComponent(n)}/mute`, { method: 'POST' })
      if (!r.ok) throw new Error('mute failed'); return r.json()
    },
    onSuccess: invalidateAll,
  })
  const unmuteSrc = useMutation({
    mutationFn: async (n: string) => {
      const r = await fetch(`/api/feed/sources/${encodeURIComponent(n)}/unmute`, { method: 'POST' })
      if (!r.ok) throw new Error('unmute failed'); return r.json()
    },
    onSuccess: invalidateAll,
  })

  const categories = catsData?.categories || []
  const sources = srcData?.sources || []
  const mutedCatCount = categories.filter(c => c.muted).length
  const mutedSrcCount = sources.filter(s => s.muted).length

  return (
    <div style={{
      minHeight: '100%', background: 'var(--bg-base)', color: 'var(--text-primary)',
      padding: '40px 32px 64px', display: 'flex', justifyContent: 'center',
    }}>
      <div style={{ maxWidth: 720, width: '100%' }}>
        <div style={{ marginBottom: 8 }}>
          <Link to="/feed" style={{ fontSize: 12, color: 'var(--text-muted)', textDecoration: 'none' }}>
            ← Back to Feed
          </Link>
        </div>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: 32, marginBottom: 8 }}>
          Feed Settings
        </h1>
        <p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 32, fontStyle: 'italic' }}>
          The master control panel for what shows up in your feed.
        </p>

        {/* Categories — primary panel */}
        <div style={{
          padding: '22px 24px', marginBottom: 14,
          background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: 22 }}>
              Feeds
            </h2>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {categories.length} categories · {mutedCatCount} muted
            </span>
          </div>
          <p style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--text-secondary)', marginBottom: 18 }}>
            Mute a feed and its <strong>future cards</strong> stop appearing. Unmuting starts the flow again from that moment forward — you won't get a backfill of what you missed during the mute. Cards already in your feed when you mute are not removed.
          </p>

          {catsLoading && <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '20px 0' }}>Loading…</div>}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {categories.map(c => (
              <div key={c.key} style={{
                display: 'flex', alignItems: 'flex-start', gap: 14,
                padding: '14px 16px',
                background: c.muted ? 'rgba(248, 81, 73, 0.05)' : 'var(--bg-elevated)',
                border: c.muted ? '1px solid rgba(248, 81, 73, 0.3)' : '1px solid var(--border)',
                borderRadius: 8,
              }}>
                <div style={{
                  flexShrink: 0, width: 36, height: 36, borderRadius: 8,
                  background: CATEGORY_COLORS[c.key] || '#856FC8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'Playfair Display, serif', fontSize: 18, color: '#fff', fontWeight: 700,
                }}>
                  {c.label[0]}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{c.label}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {c.pending_count} pending · newest {relativeTime(c.newest_at)}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, lineHeight: 1.45, color: 'var(--text-secondary)', marginBottom: 6 }}>
                    {c.description}
                  </div>
                  {c.senders.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>From:</span>
                      {c.senders.slice(0, 12).map(s => (
                        <PersonaAvatar key={s} name={s} size={18} title={s} />
                      ))}
                      {c.senders.length > 12 && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>+{c.senders.length - 12}</span>}
                    </div>
                  )}
                  {c.muted && (
                    <div style={{ marginTop: 6, fontSize: 11, color: '#f85149' }}>
                      Muted {relativeTime(c.muted_at)} · new cards from this feed are hidden
                    </div>
                  )}
                </div>
                <button
                  onClick={() => c.muted ? unmuteCat.mutate(c.key) : muteCat.mutate(c.key)}
                  disabled={muteCat.isPending || unmuteCat.isPending}
                  style={{
                    flexShrink: 0,
                    padding: '7px 16px', fontSize: 12, fontWeight: 600,
                    background: c.muted ? 'transparent' : '#b8324a',
                    color: c.muted ? 'var(--text-secondary)' : '#fff',
                    border: c.muted ? '1px solid var(--border)' : '1px solid transparent',
                    borderRadius: 6, cursor: 'pointer', minWidth: 80,
                  }}
                >
                  {c.muted ? 'Unmute' : 'Mute'}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Personas — collapsible secondary panel */}
        <div style={{
          padding: 0, marginBottom: 14,
          background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 12,
          overflow: 'hidden',
        }}>
          <button
            onClick={() => setSourcesOpen(o => !o)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 22px', background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--text-primary)', fontSize: 15, fontWeight: 500,
              fontFamily: 'Playfair Display, serif',
            }}
          >
            <span>Personas <span style={{ color: 'var(--text-muted)', fontSize: 12, fontFamily: 'inherit', marginLeft: 8 }}>(advanced — mute by sender)</span></span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {mutedSrcCount > 0 && <span style={{ fontSize: 11, color: '#f85149' }}>{mutedSrcCount} muted</span>}
              <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>{sourcesOpen ? '▾' : '▸'}</span>
            </span>
          </button>
          {sourcesOpen && (
            <div style={{ padding: '0 22px 18px' }}>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
                Muting a persona hides every card from them regardless of category. Same from-now-forward semantics as feeds above.
              </p>
              {srcLoading && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading…</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {sources.map(s => (
                  <div key={s.name} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 12px',
                    background: s.muted ? 'rgba(248, 81, 73, 0.05)' : 'transparent',
                    border: s.muted ? '1px solid rgba(248, 81, 73, 0.3)' : '1px solid transparent',
                    borderRadius: 6,
                  }}>
                    <PersonaAvatar name={s.name} size={24} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', textTransform: 'capitalize' }}>{s.name}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                        {s.pending_count} pending
                        {s.muted && <> · <span style={{ color: '#f85149' }}>muted {relativeTime(s.muted_at)}</span></>}
                      </div>
                    </div>
                    <button
                      onClick={() => s.muted ? unmuteSrc.mutate(s.name) : muteSrc.mutate(s.name)}
                      disabled={muteSrc.isPending || unmuteSrc.isPending}
                      style={{
                        padding: '4px 12px', fontSize: 11, fontWeight: 500,
                        background: s.muted ? 'transparent' : 'var(--bg-elevated)',
                        color: s.muted ? 'var(--text-secondary)' : 'var(--text-secondary)',
                        border: '1px solid var(--border)', borderRadius: 5, cursor: 'pointer',
                        minWidth: 64,
                      }}
                    >
                      {s.muted ? 'Unmute' : 'Mute'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Phase 2 placeholders */}
        <h3 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: 17, marginBottom: 10, color: 'var(--text-secondary)', marginTop: 24 }}>
          Coming next
        </h3>
        <PhaseCard title="Cadence per feed" body="Real-time, batched every few hours, or a Morning Digest only. Set independently per feed." />
        <PhaseCard title="Priority &amp; surfacing" body="Which cards flash in front, which wait. Auto-park low-priority cards older than N days. Auto-surface urgent cards." />
        <PhaseCard title="Layout" body="Card size, font, swipe sensitivity, keyboard shortcut remap." />
        <PhaseCard title="Sync" body="Already live: every choice you make here syncs to the iPhone app via the shared preferences object." live />

        <div style={{
          marginTop: 24, padding: 14, fontSize: 11, lineHeight: 1.6,
          background: 'var(--bg-elevated)', border: '1px solid var(--border)',
          borderRadius: 8, color: 'var(--text-muted)',
        }}>
          Preferences live at <code>~/.gathering/feed-preferences/joel.json</code>. The iPhone app will read the same object — when Forge wires GaPMaS iOS to it, mutes set here apply there too.
        </div>
      </div>
    </div>
  )
}

const CATEGORY_COLORS: Record<string, string> = {
  reflection: '#856FC8',
  idea: '#EE6802',
  news: '#4493D4',
  other: '#5a5a64',
}

function PhaseCard({ title, body, live }: { title: string; body: string; live?: boolean }) {
  return (
    <div style={{
      padding: '16px 20px', marginBottom: 12,
      background: 'var(--bg-surface)', border: '1px solid var(--border)',
      borderRadius: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <h3 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: 16, color: 'var(--text-primary)' }}>
          {title}
        </h3>
        <span style={{
          fontSize: 9, textTransform: 'uppercase', letterSpacing: 1,
          padding: '2px 6px', borderRadius: 3,
          background: live ? 'rgba(86,176,118,0.15)' : 'var(--bg-elevated)',
          color: live ? '#56b076' : 'var(--text-muted)',
          border: live ? '1px solid rgba(86,176,118,0.3)' : '1px solid var(--border)',
        }}>
          {live ? 'Live' : 'Phase 2'}
        </span>
      </div>
      <p style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--text-secondary)' }} dangerouslySetInnerHTML={{ __html: body }} />
    </div>
  )
}
